import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  CaretLeft, User, Ruler, Barbell, Lightning, Fire, Timer, ArrowsClockwise,
  Heart, Brain, Trophy, FloppyDisk, ChartLineUp, UploadSimple, Books, FilePdf,
  Share, Copy, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveMedia = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return u;
};

const TABS = [
  { id: "general",         label: "General",         icon: User },
  { id: "body",            label: "Body Comp",       icon: Ruler },
  { id: "mobility",        label: "Mobility",        icon: ArrowsClockwise },
  { id: "movement",        label: "Movement",        icon: Fire },
  { id: "strength",        label: "Strength",        icon: Barbell },
  { id: "power",           label: "Power",           icon: Lightning },
  { id: "speed",           label: "Speed",           icon: Timer },
  { id: "agility",         label: "Agility",         icon: ArrowsClockwise },
  { id: "conditioning",    label: "Conditioning",    icon: Heart },
  { id: "recovery",        label: "Recovery",        icon: Brain },
  { id: "sport",           label: "Sport-Specific",  icon: Trophy },
  { id: "graphs",          label: "Graphs",          icon: ChartLineUp },
  { id: "tests",           label: "Test Log",        icon: ChartLineUp },
];

// Field configs: [category_state_key, [{key, label, unit, hint}, ...]]
const FIELDS = {
  general: [
    { key: "age", label: "Age", unit: "years" },
    { key: "height_cm", label: "Height", unit: "cm" },
    { key: "weight_kg", label: "Weight", unit: "kg" },
    { key: "sport", label: "Sport" },
    { key: "event", label: "Event" },
    { key: "position", label: "Playing Position" },
    { key: "training_age", label: "Training Age", unit: "years" },
    { key: "dominant_side", label: "Dominant Side" },
    { key: "previous_injuries", label: "Previous Injuries", multiline: true },
    { key: "goals", label: "Goals", multiline: true },
    { key: "competition_date_field", label: "Competition Date", type: "date" },
  ],
  body: [
    { key: "weight_kg", label: "Weight", unit: "kg" },
    { key: "body_fat_pct", label: "Body Fat", unit: "%" },
    { key: "lean_mass_kg", label: "Lean Mass", unit: "kg" },
    { key: "muscle_mass_kg", label: "Muscle Mass", unit: "kg" },
    { key: "waist_cm", label: "Waist", unit: "cm" },
    { key: "chest_cm", label: "Chest", unit: "cm" },
    { key: "hip_cm", label: "Hip", unit: "cm" },
    { key: "arm_cm", label: "Arm", unit: "cm" },
    { key: "thigh_cm", label: "Thigh", unit: "cm" },
    { key: "calf_cm", label: "Calf", unit: "cm" },
  ],
  power: [
    { key: "cmj_cm", label: "CMJ", unit: "cm" },
    { key: "sj_cm", label: "SJ", unit: "cm" },
    { key: "broad_jump_cm", label: "Broad Jump", unit: "cm" },
    { key: "vertical_cm", label: "Vertical", unit: "cm" },
    { key: "mb_throw_m", label: "Med Ball Throw", unit: "m" },
    { key: "rsi", label: "Reactive Strength Index" },
    { key: "peak_power_w", label: "Peak Power", unit: "W" },
  ],
  speed: [
    { key: "s5m", label: "5m", unit: "s" },
    { key: "s10m", label: "10m", unit: "s" },
    { key: "s20m", label: "20m", unit: "s" },
    { key: "s30m", label: "30m", unit: "s" },
    { key: "flying_sprint", label: "Flying Sprint", unit: "s" },
    { key: "repeated_sprint_best", label: "Repeated Sprint Best", unit: "s" },
    { key: "reaction_time", label: "Reaction Time", unit: "ms" },
    { key: "acceleration", label: "Acceleration Score" },
  ],
  agility: [
    { key: "t505_s", label: "505", unit: "s" },
    { key: "t_test_s", label: "T-Test", unit: "s" },
    { key: "illinois_s", label: "Illinois", unit: "s" },
    { key: "reactive_agility", label: "Reactive Agility", unit: "s" },
    { key: "notes", label: "Sport-Specific Agility Notes", multiline: true },
  ],
  conditioning: [
    { key: "yoyo_level", label: "YoYo Level" },
    { key: "mas_kmh", label: "MAS", unit: "km/h" },
    { key: "bronco_s", label: "Bronco", unit: "s" },
    { key: "cooper_m", label: "Cooper", unit: "m" },
    { key: "beep_level", label: "Beep Test Level" },
    { key: "hr_recovery_bpm", label: "HR Recovery (1min)", unit: "bpm" },
    { key: "lactate_mmol", label: "Blood Lactate", unit: "mmol/L" },
    { key: "vo2_estimate", label: "VO₂ Est.", unit: "ml/kg/min" },
  ],
  recovery: [
    { key: "hrv_ms", label: "HRV", unit: "ms" },
    { key: "resting_hr", label: "Resting HR", unit: "bpm" },
    { key: "sleep_hours", label: "Sleep", unit: "hrs" },
    { key: "stress", label: "Stress", unit: "/10" },
    { key: "mood", label: "Mood", unit: "/10" },
    { key: "fatigue", label: "Fatigue", unit: "/10" },
    { key: "soreness", label: "Muscle Soreness", unit: "/10" },
    { key: "hydration_l", label: "Hydration", unit: "L" },
  ],
};

// Mobility joints (each is its own card with sub-fields)
const MOBILITY_JOINTS = [
  { id: "shoulder", label: "Shoulder", fields: ["flexion", "internal_rotation", "external_rotation"] },
  { id: "hip",      label: "Hip",      fields: ["flexion", "extension", "rotation"] },
  { id: "ankle",    label: "Ankle",    fields: ["dorsiflexion"] },
  { id: "tspine",   label: "T-Spine",  fields: ["rotation"] },
];

// Strength tests supported (each row: exercise + protocol + attempts + best + est 1RM etc.)
const STRENGTH_TESTS = [
  "Back Squat", "Front Squat", "Bench Press", "Deadlift", "Trap Bar",
  "Pull-up", "Push-up", "Mid-Thigh Pull", "Grip Strength",
];

export default function ClientAssessment() {
  const { client_id } = useParams();
  const [tab, setTab] = useState("general");
  const [state, setState] = useState({});
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [client, setClient] = useState(null);
  const [denied, setDenied] = useState(false);
  const [share, setShare] = useState(null); // {active, token, created_at, expires_at}
  const [shareBusy, setShareBusy] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const load = () => {
    api.get(`/coach/clients/${client_id}/assessment`).then(({ data }) => { setState(data); setDenied(false); }).catch((e) => {
      if (e?.response?.status === 403) setDenied(true);
    });
    api.get(`/coach/clients/${client_id}/assessment/history`).then(({ data }) => setHistory(data)).catch(() => {});
    api.get(`/coach/clients/${client_id}`).then(({ data }) => setClient(data.client)).catch(() => {});
    api.get(`/coach/clients/${client_id}/assessment/share`).then(({ data }) => setShare(data)).catch(() => {});
  };
  useEffect(load, [client_id]);

  const setCat = (cat, key, val) => setState((s) => ({ ...s, [cat]: { ...(s[cat] || {}), [key]: val } }));
  const setMob = (joint, key, val) => setState((s) => ({
    ...s, mobility: { ...(s.mobility || {}), [joint]: { ...((s.mobility || {})[joint] || {}), [key]: val } }
  }));
  const setStrength = (test, key, val) => setState((s) => ({
    ...s,
    strength: { ...(s.strength || {}), [test]: { ...((s.strength || {})[test] || {}), [key]: val } },
  }));
  const setMovement = (name, key, val) => setState((s) => ({
    ...s,
    movement_screen: { ...(s.movement_screen || {}), [name]: { ...((s.movement_screen || {})[name] || {}), [key]: val } },
  }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        general: state.general, body_composition: state.body_composition || state.body, mobility: state.mobility,
        movement_screen: state.movement_screen, strength: state.strength, power: state.power,
        speed: state.speed, agility: state.agility, conditioning: state.conditioning,
        recovery_metrics: state.recovery_metrics || state.recovery, sport_specific: state.sport_specific,
        performance_score: state.performance_score, competition_date: state.competition_date,
        last_assessment_date: new Date().toISOString().slice(0, 10),
      };
      await api.put(`/coach/clients/${client_id}/assessment`, payload);
      toast.success("Assessment saved · snapshot stored for graphs");
      load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/coach/clients/${client_id}/assessment/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `EdgeTraining_Assessment_${(client?.name || "athlete").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF export failed");
    }
  };

  const shareUrl = share?.token ? `${window.location.origin}/share/assessment/${share.token}` : "";

  const generateShare = async () => {
    setShareBusy(true);
    try {
      const { data } = await api.post(`/coach/clients/${client_id}/assessment/share`);
      setShare(data);
      toast.success("Share link ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate link");
    } finally {
      setShareBusy(false);
    }
  };

  const revokeShare = async () => {
    if (!window.confirm("Revoke this share link? Anyone with the URL will lose access.")) return;
    setShareBusy(true);
    try {
      await api.delete(`/coach/clients/${client_id}/assessment/share`);
      setShare({ active: false });
      toast.success("Link revoked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke");
    } finally {
      setShareBusy(false);
    }
  };

  const copyShare = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(
      () => toast.success("Copied to clipboard"),
      () => toast.error("Copy failed"),
    );
  };

  const renderFields = (cat, list) => {
    const stateKey = cat === "body" ? "body_composition" : cat === "recovery" ? "recovery_metrics" : cat;
    const catState = state[stateKey] || {};
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((f) => (
          <div key={f.key} data-testid={`field-${cat}-${f.key}`}>
            <label className="label-tiny text-[10px] text-text-secondary block mb-1">
              {f.label.toUpperCase()}{f.unit ? ` (${f.unit})` : ""}
            </label>
            {f.multiline ? (
              <Textarea rows={2} value={catState[f.key] || ""} onChange={(e) => setCat(stateKey, f.key, e.target.value)} />
            ) : (
              <Input type={f.type || "text"} value={catState[f.key] || ""} onChange={(e) => setCat(stateKey, f.key, e.target.value)} />
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6 lg:p-10 space-y-6" data-testid="assessment-page">
      {denied && (
        <div className="border border-red-500/40 bg-red-500/[0.05] p-4 mb-4" data-testid="assessment-denied">
          <div className="label-tiny text-red-500 mb-1">// ACCESS RESTRICTED</div>
          <div className="text-sm">The full assessment workflow is available to Independent Coaches only.</div>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link to={`/coach/clients/${client_id}`} className="text-xs label-tiny text-text-secondary hover:text-brand-accent inline-flex items-center gap-1 mb-2">
            <CaretLeft size={12} /> BACK TO CLIENT
          </Link>
          <div className="label-tiny text-brand-accent">// ASSESSMENT · {client?.name?.toUpperCase()}</div>
          <h1 className="font-heading text-4xl">Full Assessment</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <Link to="/coach/test-library" className="inline-flex items-center gap-1.5 border border-white/20 px-3 py-2.5 text-xs label-tiny tracking-wider hover:border-brand-accent hover:text-brand-accent transition" data-testid="assessment-test-library-link">
          <Books size={12} weight="duotone" /> TEST LIBRARY
        </Link>
        <button onClick={downloadPdf} className="inline-flex items-center gap-1.5 border border-white/20 px-3 py-2.5 text-xs label-tiny tracking-wider hover:border-brand-accent hover:text-brand-accent transition" data-testid="assessment-pdf-btn">
          <FilePdf size={12} weight="duotone" /> EXPORT PDF
        </button>
        <button onClick={() => setShowShare((s) => !s)} className="inline-flex items-center gap-1.5 border border-white/20 px-3 py-2.5 text-xs label-tiny tracking-wider hover:border-brand-accent hover:text-brand-accent transition" data-testid="assessment-share-btn">
          <Share size={12} weight="duotone" /> {share?.active ? (<><span className="hidden sm:inline">SHARE LINK </span>ACTIVE</>) : "SHARE"}
        </button>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-brand-accent text-surface-base px-5 py-2.5 text-sm label-tiny tracking-wider disabled:opacity-60" data-testid="assessment-save-btn">
          <FloppyDisk size={14} weight="bold" /> {saving ? "SAVING..." : "SAVE ASSESSMENT"}
        </button>
        </div>
      </div>

      {showShare && (
        <div className="tactical-card p-4" data-testid="assessment-share-panel">
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <div>
              <div className="label-tiny text-brand-accent">// PUBLIC SHARE PORTAL</div>
              <div className="text-sm text-text-secondary">
                Anyone with this link can view a read-only version of this athlete&apos;s latest assessment — no account required. Perfect for parents, referral coaches, or medical staff.
              </div>
            </div>
          </div>
          {share?.active ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Input readOnly value={shareUrl} className="flex-1 basis-full sm:basis-auto sm:min-w-[280px] font-mono text-xs" data-testid="assessment-share-url" onFocus={(e) => e.target.select()} />
                <button onClick={copyShare} className="inline-flex items-center gap-1.5 border border-white/20 px-3 py-2 text-xs label-tiny hover:border-brand-accent hover:text-brand-accent" data-testid="assessment-share-copy">
                  <Copy size={12} weight="duotone" /> COPY
                </button>
                <a href={shareUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-brand-accent text-brand-accent px-3 py-2 text-xs label-tiny hover:bg-brand-accent hover:text-surface-base" data-testid="assessment-share-open">
                  <Share size={12} weight="duotone" /> OPEN
                </a>
                <button onClick={revokeShare} disabled={shareBusy} className="inline-flex items-center gap-1.5 border border-red-400/40 text-red-500 px-3 py-2 text-xs label-tiny hover:bg-red-500/10 disabled:opacity-50" data-testid="assessment-share-revoke">
                  <Trash size={12} weight="duotone" /> REVOKE
                </button>
              </div>
              <div className="label-tiny text-text-secondary text-[10px]">
                {share.created_at ? `Created ${new Date(share.created_at).toLocaleDateString()}` : ""}
                {share.expires_at ? ` · Expires ${new Date(share.expires_at).toLocaleDateString()}` : ""}
              </div>
              <div className="flex items-center gap-2 mt-2" data-testid="assessment-share-analytics">
                <div className="label-tiny text-brand-heat">// OPENED</div>
                <span className="stat-number hot text-2xl tabular-nums">{share.view_count ?? 0}</span>
                <span className="label-tiny text-text-secondary">time{(share.view_count ?? 0) === 1 ? "" : "s"}</span>
                {share.last_viewed_at ? (
                  <span className="label-tiny text-text-secondary text-[10px] ml-2">
                    last viewed {new Date(share.last_viewed_at).toLocaleString()}
                  </span>
                ) : (
                  <span className="label-tiny text-text-secondary text-[10px] ml-2 opacity-70">
                    not opened yet
                  </span>
                )}
              </div>
            </div>
          ) : (
            <button onClick={generateShare} disabled={shareBusy} className="inline-flex items-center gap-2 bg-brand-accent text-surface-base px-4 py-2 text-xs label-tiny tracking-wider disabled:opacity-50" data-testid="assessment-share-generate">
              <Share size={12} weight="bold" /> {shareBusy ? "GENERATING..." : "GENERATE SHARE LINK"}
            </button>
          )}
        </div>
      )}

      <div className="border-b border-white/10 flex gap-1 overflow-x-auto" data-testid="assessment-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`assess-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs label-tiny tracking-wider transition border-b-2 -mb-px whitespace-nowrap ${
              tab === t.id ? "border-brand-accent text-brand-accent" : "border-transparent text-text-secondary hover:text-brand-accent"
            }`}>
            <t.icon size={12} weight="duotone" /> {t.label.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="min-h-[400px]">
        {tab === "general" && renderFields("general", FIELDS.general)}
        {tab === "body" && renderFields("body", FIELDS.body)}
        {tab === "power" && renderFields("power", FIELDS.power)}
        {tab === "speed" && renderFields("speed", FIELDS.speed)}
        {tab === "agility" && renderFields("agility", FIELDS.agility)}
        {tab === "conditioning" && renderFields("conditioning", FIELDS.conditioning)}
        {tab === "recovery" && renderFields("recovery", FIELDS.recovery)}

        {tab === "mobility" && (
          <div className="grid md:grid-cols-2 gap-4" data-testid="mobility-cards">
            {MOBILITY_JOINTS.map((j) => (
              <div key={j.id} className="border border-white/10 bg-white/[0.02] p-4">
                <div className="font-heading text-lg mb-3">{j.label.toUpperCase()}</div>
                <div className="grid grid-cols-1 gap-2">
                  {j.fields.map((f) => (
                    <div key={f}>
                      <label className="label-tiny text-[10px] text-text-secondary block mb-1">
                        {f.replace("_", " ").toUpperCase()} (°)
                      </label>
                      <Input type="number" value={(state.mobility?.[j.id]?.[f]) || ""} onChange={(e) => setMob(j.id, f, e.target.value)} data-testid={`mob-${j.id}-${f}`} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="border border-brand-accent/40 bg-brand-accent/[0.03] p-4 md:col-span-2">
              <label className="label-tiny text-[10px] text-brand-accent block mb-1">OVERALL MOBILITY SCORE (0–100)</label>
              <Input type="number" min="0" max="100" value={state.mobility?.score || ""} onChange={(e) => setMob("score", "value", e.target.value) || setState((s) => ({ ...s, mobility: { ...(s.mobility || {}), score: e.target.value } }))} data-testid="mobility-score" />
            </div>
          </div>
        )}

        {tab === "movement" && (
          <div className="space-y-3" data-testid="movement-screen-list">
            {["Overhead Squat", "Deep Squat", "Single Leg Squat", "Lunge", "Push-up", "Shoulder Reach", "Rotary Stability"].map((name) => {
              const m = state.movement_screen?.[name] || {};
              return (
                <MovementRow
                  key={name}
                  name={name}
                  data={m}
                  onChange={(key, val) => setMovement(name, key, val)}
                />
              );
            })}
          </div>
        )}

        {tab === "strength" && (
          <div className="space-y-3" data-testid="strength-tests">
            {STRENGTH_TESTS.map((t) => {
              const s = state.strength?.[t] || {};
              return (
                <div key={t} className="border border-white/10 bg-white/[0.02] p-4" data-testid={`strength-${t.replace(/\s+/g, '-').toLowerCase()}`}>
                  <div className="font-heading text-lg mb-3">{t.toUpperCase()}</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                    <Input placeholder="Warm-up" value={s.warmup || ""} onChange={(e) => setStrength(t, "warmup", e.target.value)} />
                    <Input placeholder="Attempts (e.g. 100/110/115)" value={s.attempts || ""} onChange={(e) => setStrength(t, "attempts", e.target.value)} />
                    <Input placeholder="Best (kg or reps)" value={s.best || ""} onChange={(e) => setStrength(t, "best", e.target.value)} />
                    <Input placeholder="Est. 1RM (kg)" value={s.est_1rm || ""} onChange={(e) => setStrength(t, "est_1rm", e.target.value)} />
                    <Input placeholder="Rel. Strength (× BW)" value={s.relative_strength || ""} onChange={(e) => setStrength(t, "relative_strength", e.target.value)} />
                    <Input placeholder="Previous Result" value={s.previous_result || ""} onChange={(e) => setStrength(t, "previous_result", e.target.value)} />
                    <Input placeholder="Trend (↑/↓)" value={s.trend || ""} onChange={(e) => setStrength(t, "trend", e.target.value)} />
                    <Input placeholder="Protocol Notes" value={s.protocol || ""} onChange={(e) => setStrength(t, "protocol", e.target.value)} />
                  </div>
                  <Textarea rows={2} placeholder="Coach comment" value={s.coach_comment || ""} onChange={(e) => setStrength(t, "coach_comment", e.target.value)} />
                </div>
              );
            })}
          </div>
        )}

        {tab === "sport" && (
          <div className="space-y-4" data-testid="sport-specific-tab">
            <div>
              <label className="label-tiny text-[10px] text-text-secondary block mb-1">SPORT</label>
              <select value={state.sport_specific?.sport || ""} onChange={(e) => setCat("sport_specific", "sport", e.target.value)} className="w-full bg-white/[0.03] border border-white/10 px-3 py-2 text-sm">
                <option value="">— Select sport —</option>
                <option value="swimming">Swimming (starts, turns, stroke rate, stroke count, pull strength, kick endurance, streamline)</option>
                <option value="boxing">Boxing (punch count, punch speed, reaction, round HR, lactate)</option>
                <option value="badminton">Badminton (court speed, COD, jump, shoulder strength)</option>
                <option value="golf">Golf (rotation, club speed, mobility, balance)</option>
                <option value="other">Other</option>
              </select>
            </div>
            <Textarea rows={8} placeholder="Sport-specific metrics — enter as free-form key: value per line (e.g., 'Stroke rate: 42 spm', 'Punch speed: 8.2 m/s')" value={state.sport_specific?.notes || ""} onChange={(e) => setCat("sport_specific", "notes", e.target.value)} />
          </div>
        )}

        {tab === "graphs" && <GraphsTab history={history} />}
        {tab === "tests" && <TestLogTab clientId={client_id} />}
      </div>
    </div>
  );
}

function MovementRow({ name, data, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const slug = name.replace(/\s+/g, '-').toLowerCase();
  const videoUrl = data.video_url ? resolveMedia(data.video_url) : "";
  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: res } = await api.post("/coach/assessment/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange("video_url", res.url);
      toast.success("Video uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <div className="border border-white/10 bg-white/[0.02] p-4 space-y-2" data-testid={`movement-${slug}`}>
      <div className="flex items-center justify-between">
        <div className="font-heading text-base">{name.toUpperCase()}</div>
        <label className="flex items-center gap-1 text-xs">
          <input type="checkbox" checked={!!data.pain} onChange={(e) => onChange("pain", e.target.checked)} data-testid={`movement-${slug}-pain`} /> Pain
        </label>
      </div>
      <div className="grid md:grid-cols-[1fr_auto] gap-2">
        <Input placeholder="Video URL (Loom, YouTube, or uploaded)" value={data.video_url || ""} onChange={(e) => onChange("video_url", e.target.value)} data-testid={`movement-${slug}-video-url`} />
        <label className="inline-flex items-center gap-1 border border-white/20 px-3 py-2 text-xs label-tiny cursor-pointer hover:border-brand-accent whitespace-nowrap" data-testid={`movement-${slug}-upload-label`}>
          <UploadSimple size={12} /> {uploading ? "..." : "UPLOAD"}
          <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={upload} data-testid={`movement-${slug}-upload-input`} />
        </label>
      </div>
      {videoUrl && (
        <video src={videoUrl} controls className="w-full max-h-48 border border-white/10 bg-black/40" data-testid={`movement-${slug}-preview`} />
      )}
      <Input placeholder="Compensations" value={data.compensations || ""} onChange={(e) => onChange("compensations", e.target.value)} data-testid={`movement-${slug}-compensations`} />
      <Textarea rows={2} placeholder="Coach comment" value={data.coach_comment || ""} onChange={(e) => onChange("coach_comment", e.target.value)} data-testid={`movement-${slug}-comment`} />
      <Textarea rows={2} placeholder="Corrective exercises to prescribe" value={data.corrective_exercises || ""} onChange={(e) => onChange("corrective_exercises", e.target.value)} data-testid={`movement-${slug}-correctives`} />
    </div>
  );
}

function GraphsTab({ history }) {  if (!history || history.length < 1) {
    return <div className="border border-white/10 p-10 text-center text-text-secondary" data-testid="graphs-empty">
      No snapshots yet. Save an assessment to start tracking trends.
    </div>;
  }
  // Compact spark-history for the most important scalar metrics
  const seriesKeys = [
    ["performance_score", "PERFORMANCE SCORE (%)"],
    ["body.body_fat_pct", "BODY FAT %"],
    ["power.cmj_cm", "CMJ (cm)"],
    ["speed.s10m", "10m SPRINT (s)"],
    ["strength.Back Squat.est_1rm", "BACK SQUAT 1RM (kg)"],
    ["conditioning.vo2_estimate", "VO₂ EST"],
    ["recovery_metrics.hrv_ms", "HRV (ms)"],
    ["mobility.score", "MOBILITY SCORE"],
  ];
  const pick = (o, path) => path.split(".").reduce((a, k) => a?.[k], o);
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="graphs-tab">
      {seriesKeys.map(([path, label]) => {
        const points = history.map((h) => ({ at: h.at, v: Number(pick(h.data, path)) })).filter((p) => !Number.isNaN(p.v));
        if (points.length === 0) return null;
        const max = Math.max(...points.map((p) => p.v)); const min = Math.min(...points.map((p) => p.v));
        const range = Math.max(1, max - min);
        return (
          <div key={path} className="border border-white/10 bg-white/[0.02] p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="label-tiny text-text-secondary">{label}</div>
              <div className="text-lg font-heading tabular-nums text-brand-accent">{points[points.length - 1].v}</div>
            </div>
            <div className="flex items-end gap-1 h-24">
              {points.map((p, i) => (
                <div key={i} className="flex-1 bg-brand-accent/70" style={{ height: `${((p.v - min) / range) * 100}%`, minHeight: "3px" }} title={`${p.v} · ${new Date(p.at).toLocaleDateString()}`} />
              ))}
            </div>
            <div className="text-[10px] text-text-secondary label-tiny mt-1">{points.length} snapshot{points.length === 1 ? "" : "s"}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ------------ Test Log Tab (structured performance tests) ------------ */

const TEST_CATEGORIES = [
  { id: "strength", label: "Strength" },
  { id: "power", label: "Power" },
  { id: "speed", label: "Speed" },
  { id: "agility", label: "Agility" },
  { id: "conditioning", label: "Conditioning" },
  { id: "mobility", label: "Mobility" },
  { id: "body_composition", label: "Body Composition" },
  { id: "sport_specific", label: "Sport Specific" },
];

function TestLogTab({ clientId }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterCat, setFilterCat] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get(`/coach/clients/${clientId}/tests?limit=200`),
        api.get(`/coach/clients/${clientId}/tests/summary`),
      ]);
      setRows(list);
      setSummary(sum);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [clientId]);

  const remove = async (test_id) => {
    if (!window.confirm("Delete this test entry?")) return;
    try {
      await api.delete(`/coach/clients/${clientId}/tests/${test_id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const visibleRows = filterCat === "all" ? rows : rows.filter((r) => r.category === filterCat);

  return (
    <div className="space-y-4" data-testid="test-log-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="label-tiny !text-yellow-400 text-[10px]">// PERFORMANCE TESTS</div>
          <div className="font-heading text-xl">Structured Test Log</div>
          <div className="text-xs text-text-secondary">Auto-tracks personal bests, % change, L/R asymmetry and target progress.</div>
        </div>
        <button
          onClick={() => setDialogOpen(true)}
          className="!bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold transition-colors px-4 py-2.5 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5"
          data-testid="test-log-open-btn"
        >
          + LOG TEST
        </button>
      </div>

      {summary && summary.recent_pbs?.length > 0 && (
        <div className="border !border-yellow-400/50 !bg-yellow-400/[0.06] p-3" data-testid="test-log-pb-strip">
          <div className="label-tiny text-[10px] !text-yellow-400 mb-2">// RECENT PBs</div>
          <div className="flex flex-wrap gap-2">
            {summary.recent_pbs.map((r) => (
              <span key={r.test_id} className="text-xs px-2 py-1 border !border-yellow-400 !text-yellow-400 tabular-nums" data-testid={`pb-chip-${r.test_id}`}>
                {r.test_name}: <span className="font-heading ml-0.5">{r.best_score}{r.unit || ""}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5" data-testid="test-log-filters">
        <button onClick={() => setFilterCat("all")} data-testid="test-filter-all" className={`text-[10px] label-tiny px-2 py-1 border transition ${filterCat === "all" ? "!bg-yellow-400 text-slate-900 !border-yellow-400" : "border-white/15 text-white/80 hover:!border-yellow-400"}`}>ALL <span className="opacity-60">{rows.length}</span></button>
        {TEST_CATEGORIES.map((c) => {
          const n = rows.filter((r) => r.category === c.id).length;
          if (n === 0) return null;
          return (
            <button key={c.id} onClick={() => setFilterCat(c.id)} data-testid={`test-filter-${c.id}`}
              className={`text-[10px] label-tiny px-2 py-1 border transition ${filterCat === c.id ? "!bg-yellow-400 text-slate-900 !border-yellow-400" : "border-white/15 text-white/80 hover:!border-yellow-400"}`}>
              {c.label.toUpperCase()} <span className="opacity-60">{n}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="p-6 text-sm text-text-secondary">Loading…</div>
      ) : visibleRows.length === 0 ? (
        <div className="border border-dashed border-white/15 p-8 text-center" data-testid="test-log-empty">
          <div className="text-sm text-white/70">No test entries yet.</div>
          <div className="text-[11px] text-text-secondary mt-1">Click LOG TEST to record a Broad Jump, Sprint, 1RM etc.</div>
        </div>
      ) : (
        <div className="border border-white/15 overflow-x-auto" data-testid="test-log-table">
          <table className="w-full text-xs tabular-nums">
            <thead>
              <tr className="!bg-yellow-400/10 border-b-2 !border-yellow-400/40 text-white/80">
                <th className="text-left px-3 py-2 label-tiny text-[10px]">DATE</th>
                <th className="text-left px-3 py-2 label-tiny text-[10px]">TEST</th>
                <th className="text-right px-3 py-2 label-tiny text-[10px]">BEST</th>
                <th className="text-right px-3 py-2 label-tiny text-[10px]">PREV</th>
                <th className="text-right px-3 py-2 label-tiny text-[10px]">Δ%</th>
                <th className="text-right px-3 py-2 label-tiny text-[10px]">L / R</th>
                <th className="text-right px-3 py-2 label-tiny text-[10px]">ASYM</th>
                <th className="text-right px-3 py-2 label-tiny text-[10px]">TARGET %</th>
                <th className="text-center px-3 py-2 label-tiny text-[10px]">PB</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => (
                <tr key={r.test_id} className="border-b border-white/10 hover:!bg-yellow-400/5" data-testid={`test-row-${r.test_id}`}>
                  <td className="px-3 py-2 text-white/70">{r.test_date ? new Date(r.test_date).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-2 text-white">
                    {r.test_name}
                    <span className="ml-2 text-[10px] text-text-secondary uppercase">{r.category}</span>
                  </td>
                  <td className="px-3 py-2 text-right !text-yellow-400 font-heading">{r.best_score ?? "—"}{r.unit ? <span className="text-[9px] text-text-secondary ml-0.5">{r.unit}</span> : null}</td>
                  <td className="px-3 py-2 text-right text-white/60">{r.previous_result ?? "—"}</td>
                  <td className={`px-3 py-2 text-right font-heading ${r.percent_change == null ? "text-white/50" : (r.higher_is_better ? (r.percent_change > 0 ? "text-green-400" : "text-red-400") : (r.percent_change < 0 ? "text-green-400" : "text-red-400"))}`}>{r.percent_change != null ? `${r.percent_change > 0 ? "+" : ""}${r.percent_change}%` : "—"}</td>
                  <td className="px-3 py-2 text-right text-white/70">{r.left_value != null || r.right_value != null ? `${r.left_value ?? "—"} / ${r.right_value ?? "—"}` : "—"}</td>
                  <td className={`px-3 py-2 text-right ${r.asymmetry == null ? "text-white/50" : r.asymmetry > 10 ? "text-red-400 font-bold" : "text-white/80"}`}>{r.asymmetry != null ? `${r.asymmetry}%` : "—"}</td>
                  <td className="px-3 py-2 text-right text-white/80">{r.target_progress != null ? `${r.target_progress}%` : "—"}</td>
                  <td className="px-3 py-2 text-center">{r.is_personal_best ? <span className="!text-yellow-400 font-bold" data-testid={`pb-flag-${r.test_id}`}>★</span> : ""}</td>
                  <td className="px-3 py-2 text-right"><button onClick={() => remove(r.test_id)} className="text-white/40 hover:text-red-400 transition" aria-label="Delete" data-testid={`test-delete-${r.test_id}`}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialogOpen && (
        <LogTestDialog
          clientId={clientId}
          onClose={() => setDialogOpen(false)}
          onSaved={() => { setDialogOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function LogTestDialog({ clientId, onClose, onSaved }) {
  const [form, setForm] = useState({
    category: "power", test_name: "", protocol: "", unit: "",
    attempts_str: "", left_value: "", right_value: "",
    target: "", higher_is_better: null, notes: "", video_url: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.test_name.trim()) return toast.error("Test name required");
    setSaving(true);
    try {
      const attempts = form.attempts_str
        .split(/[,\s]+/).map((x) => parseFloat(x)).filter((x) => !Number.isNaN(x));
      const body = {
        category: form.category,
        test_name: form.test_name.trim(),
        protocol: form.protocol || null,
        unit: form.unit || null,
        attempts: attempts.length ? attempts : null,
        left_value: form.left_value !== "" ? parseFloat(form.left_value) : null,
        right_value: form.right_value !== "" ? parseFloat(form.right_value) : null,
        target: form.target !== "" ? parseFloat(form.target) : null,
        higher_is_better: form.higher_is_better,
        notes: form.notes || null,
        video_url: form.video_url || null,
      };
      const { data } = await api.post(`/coach/clients/${clientId}/tests`, body);
      const pctTxt = data.percent_change != null ? ` (${data.percent_change > 0 ? "+" : ""}${data.percent_change}%)` : "";
      toast.success(`${data.test_name}: ${data.best_score}${data.unit || ""}${pctTxt}${data.is_personal_best ? " ★ PB" : ""}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Log failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="log-test-dialog">
      <div className="bg-surface-DEFAULT border border-white/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-white/15">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-tiny !text-yellow-400 text-[10px]">// LOG TEST</div>
              <div className="font-heading text-2xl text-white">Record a Test Result</div>
            </div>
            <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="log-test-close">✕</button>
          </div>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-tiny text-[10px] text-white/70 block mb-1">CATEGORY</label>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="log-test-category" className="w-full bg-surface-elevated border border-white/20 h-9 px-2 text-sm">
                {TEST_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-tiny text-[10px] text-white/70 block mb-1">TEST NAME</label>
              <Input value={form.test_name} onChange={(e) => set("test_name", e.target.value)} placeholder="e.g. Broad Jump" data-testid="log-test-name" className="bg-surface-elevated border-white/20 h-9 text-sm" />
            </div>
          </div>
          <div>
            <label className="label-tiny text-[10px] text-white/70 block mb-1">PROTOCOL / EQUIPMENT NOTES</label>
            <Input value={form.protocol} onChange={(e) => set("protocol", e.target.value)} placeholder="e.g. NSCA standard, contact mat" data-testid="log-test-protocol" className="bg-surface-elevated border-white/20 h-9 text-sm" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label-tiny text-[10px] text-white/70 block mb-1">ATTEMPTS (comma-sep)</label>
              <Input value={form.attempts_str} onChange={(e) => set("attempts_str", e.target.value)} placeholder="2.15, 2.22, 2.18" data-testid="log-test-attempts" className="bg-surface-elevated border-white/20 h-9 text-sm" />
            </div>
            <div>
              <label className="label-tiny text-[10px] text-white/70 block mb-1">UNIT</label>
              <Input value={form.unit} onChange={(e) => set("unit", e.target.value)} placeholder="m / cm / s / kg" data-testid="log-test-unit" className="bg-surface-elevated border-white/20 h-9 text-sm" />
            </div>
            <div>
              <label className="label-tiny text-[10px] text-white/70 block mb-1">TARGET</label>
              <Input type="number" step="0.01" value={form.target} onChange={(e) => set("target", e.target.value)} placeholder="2.5" data-testid="log-test-target" className="bg-surface-elevated border-white/20 h-9 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label-tiny text-[10px] text-white/70 block mb-1">LEFT (asymmetry)</label>
              <Input type="number" step="0.01" value={form.left_value} onChange={(e) => set("left_value", e.target.value)} placeholder="32.5" data-testid="log-test-left" className="bg-surface-elevated border-white/20 h-9 text-sm" />
            </div>
            <div>
              <label className="label-tiny text-[10px] text-white/70 block mb-1">RIGHT</label>
              <Input type="number" step="0.01" value={form.right_value} onChange={(e) => set("right_value", e.target.value)} placeholder="36.0" data-testid="log-test-right" className="bg-surface-elevated border-white/20 h-9 text-sm" />
            </div>
            <div>
              <label className="label-tiny text-[10px] text-white/70 block mb-1">DIRECTION</label>
              <select value={form.higher_is_better === null ? "auto" : String(form.higher_is_better)}
                onChange={(e) => set("higher_is_better", e.target.value === "auto" ? null : e.target.value === "true")}
                data-testid="log-test-direction"
                className="w-full bg-surface-elevated border border-white/20 h-9 px-2 text-sm">
                <option value="auto">Auto (from name)</option>
                <option value="true">Higher is better</option>
                <option value="false">Lower is better</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label-tiny text-[10px] text-white/70 block mb-1">VIDEO URL (optional)</label>
            <Input value={form.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="https://youtu.be/..." data-testid="log-test-video" className="bg-surface-elevated border-white/20 h-9 text-sm" />
          </div>
          <div>
            <label className="label-tiny text-[10px] text-white/70 block mb-1">NOTES</label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} placeholder="Coach cues, conditions, standout observations…" data-testid="log-test-notes" className="bg-surface-elevated border-white/20 text-sm" />
          </div>
        </div>
        <div className="p-5 border-t border-white/15 flex gap-2">
          <button onClick={onClose} className="flex-1 border border-white/20 text-white/80 py-2.5 text-xs label-tiny tracking-widest hover:!border-yellow-400" data-testid="log-test-cancel">CANCEL</button>
          <button onClick={submit} disabled={saving} className="flex-1 !bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold py-2.5 text-xs label-tiny tracking-widest disabled:opacity-50" data-testid="log-test-save">
            {saving ? "SAVING…" : "SAVE TEST"}
          </button>
        </div>
      </div>
    </div>
  );
}

